import { useState, useEffect } from 'react';
import { Header } from './Header';
import { ViewTickets } from './ViewTickets';
import { useSupportTicket } from '../../hooks/SupportTicket/useSupportTicket';
import { SupportTicket } from '../../types/commonTypes';
import { useOperator } from '../../contexts/OperatorContext';
import { useUser } from '../../contexts/UserContext';
import { NoDataMessage } from '../../components/NoDataMessage';

export const SupportPage = () => {
  const [activeSite, setActiveSite] = useState<string | null>(null);
  const { getTickets, loading, tickets } = useSupportTicket(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date(),
  });
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [pendingOnly, setPendingOnly] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('');
  const [updateSubmitted, setUpdateSubmitted] = useState(false);
  const user = useUser();
  const operator = useOperator();

  useEffect(() => {
    if (operator.isChanging) setActiveSite(null);
  }, [operator.isChanging]);

  useEffect(() => {
    if (tickets.length !== 0 && !loading) {
      switch (selectedMetric) {
        case 'open':
          setFilteredTickets(
            tickets.filter(
              (ticket: SupportTicket) =>
                ticket.behavior.toLowerCase() !== 'closed'
            )
          );
          break;
        case 'incident':
          setFilteredTickets(
            tickets.filter(
              (ticket: SupportTicket) =>
                ticket.categoryName.toLowerCase() === 'incident'
            )
          );
          break;
        case 'question/request':
          setFilteredTickets(
            tickets.filter(
              (ticket: SupportTicket) =>
                ticket.categoryName.toLowerCase() === 'question' ||
                ticket.categoryName.toLowerCase() === 'request'
            )
          );
          break;
        case 'other':
          setFilteredTickets(
            tickets.filter(
              (ticket: SupportTicket) =>
                ticket.categoryName.toLowerCase() !== 'question' &&
                ticket.categoryName.toLowerCase() !== 'request' &&
                ticket.categoryName.toLowerCase() !== 'incident'
            )
          );
          break;
        default:
          setFilteredTickets(tickets);
      }
    }
  }, [tickets, selectedMetric, loading]);

  useEffect(() => {
    setFilteredTickets(tickets);
  }, [tickets]);

  useEffect(() => {
    setSelectedMetric('');
  }, [pendingOnly]);

  useEffect(() => {
    setTimeout(
      () => {
        getTickets(
          pendingOnly,
          new Date(dateRange.start.toISOString()),
          new Date(dateRange.end.toISOString()),
          activeSite
        );
        setUpdateSubmitted(false);
      },
      updateSubmitted ? 4000 : 0
    );
  }, [operator.id, dateRange, pendingOnly, activeSite, updateSubmitted]);

  if (
    (operator.type === 'Residential' && !user.permissions.canResSupportView) ||
    (operator.type === 'Hospitality' && !user.permissions.canHosSupportView)
  )
    return (
      <NoDataMessage
        title="No Access"
        subTitle="You do not have permission to view Support Tickets"
      />
    );

  const handleCreateTicket = () => {
    getTickets(
      pendingOnly,
      new Date(dateRange.start.toISOString()),
      new Date(dateRange.end.toISOString()),
      activeSite
    );
  };

  return (
    <div className="flex flex-col items-center justify-evenly">
      <Header
        ticketData={tickets}
        loading={loading}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
        onCreateTicket={handleCreateTicket}
      />
      <ViewTickets
        ticketData={filteredTickets}
        loading={loading}
        setActiveSite={setActiveSite}
        dateRange={dateRange}
        setDateRange={setDateRange}
        pendingOnly={pendingOnly}
        setViewAll={setPendingOnly}
        updateSubmitted={updateSubmitted}
        setUpdateSubmitted={setUpdateSubmitted}
      />
    </div>
  );
};
